import {
  TextField,
  Theme,
  useMediaQuery,
  Typography,
  Collapse,
  CircularProgress,
} from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { PrimaryButton } from 'sharedComponents/Buttons';
import { Label } from 'sharedComponents/Label';
import { ConnectMarketingInputAndButtonProps } from '../types';
import { ConnectMarketingInputAndButtonDataTestIds } from 'data-testids/ConnectDataTestIds';
import { useTheme } from '@mui/material/styles';
import GoogleLogin from 'components/Login/LoginForm/GoogleLogin/GoogleLogin';
import { googleNameError } from 'utils/message';
import { useHistory } from 'react-router-dom';
import { useStateParam } from 'features/Connect/features/Context/ConnectStateCodeContextProvider';
import { CONNECT_JOBBOARD_STATES } from 'utils/constants';
import { connectRedirectUrls, connectUrlParams } from 'utils/constants/urls';
import { buildUrl } from 'utils/util';
import { useGoogleSignIn } from 'hooks/useGoogleSignIn';
import { GoogleSSOButtonType, ConnectGatedActions } from 'features/Connect/utils/connectEnums';
import { captureException } from '@sentry/browser';
import { nimbleProduct } from 'types/types';

interface GoogleButtonBoxProps {
  showSignUp: boolean;
}

export function ConnectMarketingInputAndButton({
  onSubmit,
  isLoading,
  email,
  setEmail,
  isActive,
}: ConnectMarketingInputAndButtonProps): React.ReactElement {
  const theme = useTheme();
  const history = useHistory();
  const stateCode = useStateParam();

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const showSignUp = email.length > 0;
  const redirectUrl = buildUrl(connectRedirectUrls.PROFILE, {
    [connectUrlParams.REDIRECT]: CONNECT_JOBBOARD_STATES[stateCode].urlParam,
  });

  const redirectToProfile = () => {
    history.push(redirectUrl);
  };

  const reloadWindow = () => window.location.reload();

  const { handleGoogleSignIn, isGoogleLoading } = useGoogleSignIn({
    onSuccess: (user) => {
      const isProfileComplete = user?.preference?.nimble_connect_profile?.is_completed;
      isProfileComplete ? (onSubmit(), reloadWindow()) : redirectToProfile();
    },
    onError: (error) => {
      const status = error?.response?.status;
      status === 500 ? googleNameError() : captureException(error);
    },
    additionalData: {
      nimble_product: nimbleProduct.connect,
      signup_action: ConnectGatedActions.SIGNUP_BANNER,
      redirect_to: redirectUrl,
      state: CONNECT_JOBBOARD_STATES[stateCode].urlParam,
    },
  });

  const promptContainerId = 'google-one-tap-container';

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const isButtonEnabled = isActive && !isLoading;
    if (event.key === 'Enter' && isButtonEnabled) {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <PromptContainer id={promptContainerId}>
      {isGoogleLoading ? (
        <ConnectMarketingInputAndButtonContainer sx={{ justifyContent: 'center' }}>
          <CircularProgress />
        </ConnectMarketingInputAndButtonContainer>
      ) : (
        <ConnectMarketingInputAndButtonContainer>
          <GoogleButtonGrid>
            <Collapse
              in={!showSignUp}
              orientation="horizontal"
              sx={{ gridArea: '1 / 1', width: '100%' }}
            >
              <GoogleLogin
                googleSignin={handleGoogleSignIn}
                buttonType={GoogleSSOButtonType.PILL}
                promptParentId={promptContainerId}
                dataTestId={ConnectMarketingInputAndButtonDataTestIds.GOOGLE_LOGIN_BUTTON_PILL}
              />
            </Collapse>
            <GoogleButtonBox showSignUp={showSignUp}>
              <GoogleLogin
                googleSignin={handleGoogleSignIn}
                buttonType={GoogleSSOButtonType.ICON}
                promptParentId={promptContainerId}
                dataTestId={ConnectMarketingInputAndButtonDataTestIds.GOOGLE_LOGIN_BUTTON_ICON}
              />
            </GoogleButtonBox>
          </GoogleButtonGrid>

          <VerticalDividerWithText />

          <InputLabelBox>
            <Label
              labelText={isMobile ? 'Enter personal email' : 'Enter your personal email'}
              sx={labelStyles(isMobile, theme)}
            />
            <StyledTextField
              type="email"
              inputProps={{ style: { padding: 0 } }}
              placeholder={isMobile ? 'Personal email' : 'Personal email address'}
              onChange={(evt) => setEmail(evt.target.value)}
              onKeyDown={handleKeyDown}
              value={email}
              data-testid={ConnectMarketingInputAndButtonDataTestIds.EMAIL_INPUT}
            />
          </InputLabelBox>

          <Collapse in={showSignUp} orientation="horizontal" timeout={300}>
            <SignUpButtonContainer>
              <Divider />
              <SignUpButtonInnerBox>
                <PrimaryButton
                  type="submit"
                  onClick={onSubmit}
                  size="large"
                  isLoading={isLoading || isGoogleLoading}
                  disabled={!isActive || isGoogleLoading}
                  dataTestId={ConnectMarketingInputAndButtonDataTestIds.SIGN_UP_BUTTON}
                  sx={primaryButtonStyles(theme)}
                >
                  Sign up
                </PrimaryButton>
              </SignUpButtonInnerBox>
            </SignUpButtonContainer>
          </Collapse>
        </ConnectMarketingInputAndButtonContainer>
      )}
    </PromptContainer>
  );
}

const VerticalDividerWithText = () => {
  const theme = useTheme();
  return (
    <StyledVerticalDividerWithText>
      <StyledDividerLine theme={theme} />
      <OrTypography variant="caption">or</OrTypography>
      <StyledDividerLine theme={theme} />
    </StyledVerticalDividerWithText>
  );
};

const PromptContainer = styled(Box)({});

const GoogleButtonGrid = styled(Box)({
  display: 'grid',
  alignItems: 'center',
  justifyItems: 'start',
});

const GoogleButtonBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showSignUp',
})<GoogleButtonBoxProps>(({ showSignUp }) => ({
  gridArea: '1 / 1',
  opacity: showSignUp ? 1 : 0,
  transition: 'opacity 0.3s ease-in-out',
  transitionDelay: showSignUp ? '100ms' : '0ms',
}));

const StyledVerticalDividerWithText = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '40px',
  marginLeft: '2px',
  marginRight: '2px',
});

const StyledDividerLine = styled(Box)(({ theme }) => ({
  borderLeft: `1px solid ${theme.palette.divider}`,
  flexGrow: 1,
}));

const OrTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
}));

const SignUpButtonContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
});

const SignUpButtonInnerBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 1,
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  color: '#6B7280',
  lineHeight: '20px',
  padding: 'none',
  fontStyle: 'normal',
  fontSize: theme.typography.h3.fontSize,
  '& fieldset': { border: 'none' },
  width: '100%',

  [theme.breakpoints.down('md')]: {
    '& .MuiInputBase-input': {
      fontSize: theme.typography.h3.fontSize,
    },
    '& .MuiInputBase-input::placeholder': {
      fontSize: theme.typography.body3.fontSize,
    },
  },
}));

const ConnectMarketingInputAndButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  border: `${theme.spacing(1)} solid ${theme.palette.primary.fill}`,
  borderRadius: theme.shape.borderRadius * 6,
  alignItems: 'center',
  padding: theme.spacing(0, 1, 0, 1),
  minHeight: '86px',
  maxHeight: '86px',
  gap: theme.spacing(1),
  backgroundColor: theme.palette.white.main,

  [theme.breakpoints.between('sm', 'lg')]: {
    minHeight: '70px',
    maxHeight: '70px',
    gap: theme.spacing(2),
  },

  [theme.breakpoints.down('sm')]: {
    minHeight: '65px',
    maxHeight: '65px',
    gap: theme.spacing(1),
  },
}));

const InputLabelBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const Divider = styled(Box)(({ theme }) => ({
  border: `0.5px solid ${theme.palette.divider}`,
  color: '#6B7280',

  [theme.breakpoints.down('md')]: {
    height: '22.67px',
  },
  [theme.breakpoints.up('md')]: {
    height: '32px',
  },
}));

const labelStyles = (isMobile, theme) => ({
  color: theme.palette.black.darker,
  fontWeight: theme.typography.fontWeightBold,
  fontSize: isMobile ? '8.5px' : '12px',
  paddingBottom: 'none',
  padding: 'none',
});

const primaryButtonStyles = (theme) => ({
  color: theme.palette.white.main,
  maxWidth: '180px',
  maxHeight: '53px',
  fontWeight: theme.typography.fontWeightMedium,
  fontSize: '16px',
  borderRadius: theme.shape.borderRadius,
  padding: '13.393px 21.429px',
  whiteSpace: 'nowrap',
  gap: theme.spacing(1),

  [theme.breakpoints.down('lg')]: {
    maxWidth: '120px',
    maxHeight: '40px',
    fontSize: '11.33px',
    borderRadius: '11.33px',
    padding: `10.625px ${theme.spacing(1)}`,
    gap: theme.spacing(0.75),
    background: '#0071BA',
    display: 'inline-flex',
    alignItems: 'center',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '11.33px',
    lineHeight: '18.7px',
    gap: theme.spacing(0.5),
  },
});
